import moment from 'moment';
import Filters, { registerModuleFilters } from '@/utils/input/filters';
import { CoreEnums } from '@/utils/domain/constants';
import { createEnumOptions } from '@/utils/core/data-transformation';
import { getEnumNamespace } from '@/utils/infrastructure';

const { UserFilter, BaseFilters, DateFilter } = Filters;
const { DelegationType, DelegationStatus } = CoreEnums;

// Badge configurations for table rendering
export const delegationStatus = {
  [DelegationStatus.Pending]: {
    id: 'delegations.delegationStatus.pending',
    color: 'warning',
  },
  [DelegationStatus.Execution]: {
    id: 'delegations.delegationStatus.execution',
    color: 'execution',
  },
  [DelegationStatus.Finished]: {
    id: 'delegations.delegationStatus.finished',
    color: 'success',
  },
  [DelegationStatus.Voided]: {
    id: 'delegations.delegationStatus.voided',
    color: 'voided',
  },
  [DelegationStatus.Error]: {
    id: 'delegations.delegationStatus.error',
    color: 'error',
  },
};

export const delegationType = {
  [DelegationType.Temporary]: {
    id: 'delegations.delegationType.temporary',
    color: 'warning',
  },
  [DelegationType.Definitive]: {
    id: 'delegations.delegationType.definitive',
    color: 'success',
  },
};

export const DelegationStartDateConstraints = Object.freeze({
  LEAD_MINUTES: 30,
  VALIDATION_MESSAGE_ID: 'delegations.form.startDateMinLead',
});

const range0To = (exclusiveEnd) => (exclusiveEnd > 0 ? [...Array(exclusiveEnd).keys()] : []);

const fullMinuteOrSecondRange = () => [...Array(60).keys()];

export const getDelegationStartMinMoment = () =>
  moment().add(DelegationStartDateConstraints.LEAD_MINUTES, 'minutes');

/** Earliest valid time with seconds 00 that is still >= raw min (now + lead). */
export const getDelegationStartMinMomentCeiledToZeroSeconds = () => {
  const min = getDelegationStartMinMoment();
  const atZero = min.clone().seconds(0).milliseconds(0);
  if (atZero.isBefore(min)) {
    return atZero.add(1, 'minute');
  }
  return atZero;
};

/** Each picker needs its own `showTime` object and cloned `defaultValue` moment. */
export const getDelegationDatePickerShowTime = () => ({
  format: 'HH:mm:ss',
  defaultValue: moment('00:00:00', 'HH:mm:ss').clone(),
});

// clone() avoids mutating antd's moment (endOf mutates); otherwise the calendar selection UI breaks.
export const getDelegationStartDisabledDate = (current) => {
  if (!current) return false;
  return current.clone().endOf('day').isBefore(getDelegationStartMinMomentCeiledToZeroSeconds());
};

export const getDelegationStartDisabledTime = (current) => {
  const min = getDelegationStartMinMomentCeiledToZeroSeconds();
  if (!current?.isSame?.(min, 'day')) return {};
  const h = min.hour();
  const mi = min.minute();
  const s = min.second();
  return {
    disabledHours: () => range0To(h),
    disabledMinutes: (hour) =>
      hour < h ? fullMinuteOrSecondRange() : hour > h ? [] : range0To(mi),
    disabledSeconds: (hour, minute) => {
      if (hour < h || (hour === h && minute < mi)) return fullMinuteOrSecondRange();
      if (hour > h || minute > mi) return [];
      return range0To(s);
    },
  };
};

/** Applies to both start and end: value must be on or after now + LEAD_MINUTES (raw; supports legacy seconds on edit). */
export const isDelegationDateAtOrAfterMinLead = (value) => {
  if (value == null || value === '') return true;
  const m = moment.isMoment(value) ? value : moment(value);
  if (!m.isValid()) return true;
  return !m.isBefore(getDelegationStartMinMoment());
};

export const DelegationEndDateConstraints = Object.freeze({
  VALIDATION_MESSAGE_ID: 'delegations.form.endDateAfterStart',
});

export const isDelegationEndAfterStart = (endValue, startValue) => {
  if (endValue == null || endValue === '' || startValue == null || startValue === '') {
    return true;
  }
  const endM = moment.isMoment(endValue) ? endValue : moment(endValue);
  const startM = moment.isMoment(startValue) ? startValue : moment(startValue);
  if (!endM.isValid() || !startM.isValid()) return true;
  return endM.isAfter(startM);
};

// Filter configurations
const DelegationTypeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'type',
  label: 'global.type',
  enumNamespace: getEnumNamespace(DelegationType),
  enumSource: DelegationType,
  options: createEnumOptions(DelegationType, 'delegations.delegationType'),
};

const DelegationStatusFilter = {
  ...BaseFilters.EnumFilter,
  name: 'status',
  label: 'global.text.status',
  enumNamespace: getEnumNamespace(DelegationStatus),
  enumSource: DelegationStatus,
  options: createEnumOptions(DelegationStatus, 'delegations.delegationStatus'),
};

const StartDateFilter = {
  ...DateFilter,
  label: 'delegations.form.startDate',
  name: 'start',
};

const EndDateFilter = {
  ...DateFilter,
  label: 'delegations.form.endDate',
  name: 'end',
};

const CreatedDateFilter = {
  ...DateFilter,
  label: 'global.text.createdDate',
};

const UpdatedDateFilter = {
  ...DateFilter,
  label: 'global.text.updatedDate',
  name: 'updatedDateRange',
};

const UserCreatorFilter = {
  ...UserFilter,
  name: 'userCreatorId',
  label: 'global.userCreator',
};

const UserModifierFilter = {
  ...UserFilter,
  name: 'userModifierId',
  label: 'global.userModifier',
};

export const filtersConfig = [
  DelegationTypeFilter,
  DelegationStatusFilter,
  { ...UserFilter, name: 'sourceUserId' },
  StartDateFilter,
  EndDateFilter,
  CreatedDateFilter,
  UserCreatorFilter,
  UpdatedDateFilter,
  UserModifierFilter,
];

registerModuleFilters('delegations', filtersConfig);
